import {
  evaluateLayerPoseAtGlobalTime,
  type LayerPose,
} from '@/animation/evaluateLayerPoseAtGlobalTime'
import { CLIP_BOUNDARY_TIME_EPS_SEC } from '@/animation/timeline/clipEndpoints'
import type { AnimationLayer } from '@/types/animationLayer'

export interface EvaluatedLayerFrame {
  visible: boolean
  pose: LayerPose
}

/**
 * Purely evaluates all render-facing layer state at an arbitrary timeline time.
 * The result depends only on layer data and time, so seeking never relies on a
 * previously rendered frame.
 */
export function evaluateLayerFrameAtGlobalTime(
  layer: AnimationLayer,
  globalTimeSeconds: number,
): EvaluatedLayerFrame {
  const parsedTime = Number(globalTimeSeconds)
  const time = Number.isFinite(parsedTime) ? parsedTime : 0
  const parsedStart = Number(layer.visibilityStartTimeSec)
  const parsedEnd = Number(layer.visibilityEndTimeSec)
  const start = Number.isFinite(parsedStart) ? parsedStart : 0
  const end = Number.isFinite(parsedEnd) ? parsedEnd : start
  let poseTime = Math.min(end, Math.max(start, time))
  const firstClip = [...layer.clips].sort((a, b) => a.startSec - b.startSec)[0]
  if (
    firstClip &&
    firstClip.startSec >= start &&
    firstClip.startSec - start <= CLIP_BOUNDARY_TIME_EPS_SEC &&
    poseTime < firstClip.startSec
  ) {
    /** Hold the entrance keyframe across tiny snap/drag gaps at the visibility boundary. */
    poseTime = firstClip.startSec
  }

  return {
    visible: time >= start && time <= end,
    /** Keep hidden layers staged at their nearest visibility boundary pose. */
    pose: evaluateLayerPoseAtGlobalTime(layer, poseTime),
  }
}
